;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/admin/generations/:kind/:id\",\"regex\":\"^/admin/generations/([^/]+)/([^/]+)$\",\"paramNames\":[\"kind\",\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/users/:id\",\"regex\":\"^/admin/users/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/blog/:slug\",\"regex\":\"^/blog/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/blog/category/:category\",\"regex\":\"^/blog/category/([^/]+)$\",\"paramNames\":[\"category\"],\"hasOptionalPrefix\":false},{\"path\":\"/blog/p/:num\",\"regex\":\"^/blog/p/([^/]+)$\",\"paramNames\":[\"num\"],\"hasOptionalPrefix\":false},{\"path\":\"/brand/:slug\",\"regex\":\"^/brand/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/compare/:competitor\",\"regex\":\"^/compare/([^/]+)$\",\"paramNames\":[\"competitor\"],\"hasOptionalPrefix\":false},{\"path\":\"/ideas/:industry\",\"regex\":\"^/ideas/([^/]+)$\",\"paramNames\":[\"industry\"],\"hasOptionalPrefix\":false},{\"path\":\"/niche/:slug\",\"regex\":\"^/niche/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/niche/:slug/@detail\",\"regex\":\"^/niche/([^/]+)/@detail$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/niche/:slug/@detail/c/:candidateId\",\"regex\":\"^/niche/([^/]+)/@detail/c/([^/]+)$\",\"paramNames\":[\"slug\",\"candidateId\"],\"hasOptionalPrefix\":false},{\"path\":\"/niche/:slug/@grid\",\"regex\":\"^/niche/([^/]+)/@grid$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/niche/:slug/c/:candidateId\",\"regex\":\"^/niche/([^/]+)/c/([^/]+)$\",\"paramNames\":[\"slug\",\"candidateId\"],\"hasOptionalPrefix\":false},{\"path\":\"/plan/:slug\",\"regex\":\"^/plan/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/start/:niche\",\"regex\":\"^/start/([^/]+)$\",\"paramNames\":[\"niche\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/admin\"},{\"path\":\"/admin/alerts\"},{\"path\":\"/admin/analytics\"},{\"path\":\"/admin/errors\"},{\"path\":\"/admin/generations\"},{\"path\":\"/admin/inbox\"},{\"path\":\"/admin/infra\"},{\"path\":\"/admin/referrals\"},{\"path\":\"/admin/reports\"},{\"path\":\"/admin/revenue\"},{\"path\":\"/admin/users\"},{\"path\":\"/admin/ux\"},{\"path\":\"/app\"},{\"path\":\"/auth\"},{\"path\":\"/blog\"},{\"path\":\"/dashboard\"},{\"path\":\"/login\"},{\"path\":\"/pricing\"},{\"path\":\"/privacy\"},{\"path\":\"/settings\"},{\"path\":\"/terms\"},{\"path\":\"/unsubscribe\"}],\"isrRoutes\":[\"/blog/:slug\",\"/blog/category/:category\",\"/blog/p/:num\",\"/compare/:competitor\",\"/ideas/:industry\",\"/start/:niche\"]}";globalThis["_sentryNextJsVersion"] = "16.2.6";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from "@sentry/nextjs";
import type { ErrorEvent } from "@sentry/nextjs";

// AUDIT-M15: PII scrub — defensive layer against accidental email leaks
// through Sentry extras / contexts / message strings on the client side
// (browser SDK). Mirrors server-side scrub in instrumentation.ts. See that
// file for rationale.
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const REDACTED = "[redacted-email]";

function redactEmails(value: unknown, depth = 0): unknown {
  if (depth > 10) return value;
  if (typeof value === "string") {
    return value.replace(EMAIL_REGEX, REDACTED);
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactEmails(item, depth + 1));
  }
  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (/^email$/i.test(key) && typeof v === "string") {
        result[key] = REDACTED;
      } else {
        result[key] = redactEmails(v, depth + 1);
      }
    }
    return result;
  }
  return value;
}

function scrubPii<E extends ErrorEvent>(event: E): E {
  if (event.extra) {
    event.extra = redactEmails(event.extra) as E["extra"];
  }
  if (event.contexts) {
    event.contexts = redactEmails(event.contexts) as E["contexts"];
  }
  if (event.tags) {
    event.tags = redactEmails(event.tags) as E["tags"];
  }
  if (event.user?.email) {
    event.user.email = REDACTED;
  }
  if (typeof event.message === "string") {
    event.message = event.message.replace(EMAIL_REGEX, REDACTED);
  }
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (typeof ex.value === "string") {
        ex.value = ex.value.replace(EMAIL_REGEX, REDACTED);
      }
    }
  }
  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      if (typeof b.message === "string") {
        b.message = b.message.replace(EMAIL_REGEX, REDACTED);
      }
      if (b.data) {
        b.data = redactEmails(b.data) as typeof b.data;
      }
    }
  }
  return event;
}

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    environment: process.env.NODE_ENV ?? "development",
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Error-only replay capture — record session video ONLY when an error
    // happens (not random sessions). Free tier: 50 replays/month total.
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    beforeSend(event) {
      if (event.request?.data) delete event.request.data;
      return scrubPii(event);
    },
  });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
